import { create } from 'zustand'
import { persist } from 'zustand/middleware'

interface UsedAddress {
  address: string
  path: string
  firstUsed: number
  lastUsed: number
}

interface WalletAddressesState {
  // Used addresses by wallet identifier (xpub hash)
  usedAddresses: Record<string, UsedAddress[]>
  
  // Mark an address as used
  markAddressUsed: (walletId: string, address: string, path: string) => void
  
  // Check if an address is used
  isAddressUsed: (walletId: string, address: string) => boolean
  
  // Get all used addresses for a wallet
  getUsedAddresses: (walletId: string) => Set<string>
  
  // Clear used addresses for a wallet
  clearWalletAddresses: (walletId: string) => void
  
  // Clear all data
  clearAll: () => void
}

export const useWalletAddresses = create<WalletAddressesState>()(
  persist(
    (set, get) => ({
      usedAddresses: {},
      
      markAddressUsed: (walletId: string, address: string, path: string) => {
        set((state) => {
          const walletAddresses = state.usedAddresses[walletId] || []
          const existing = walletAddresses.find(a => a.address === address)
          
          if (existing) {
            // Update last used timestamp
            return {
              usedAddresses: {
                ...state.usedAddresses,
                [walletId]: walletAddresses.map(a => 
                  a.address === address 
                    ? { ...a, lastUsed: Date.now() }
                    : a
                )
              }
            }
          } else {
            // Add new used address
            const now = Date.now()
            return {
              usedAddresses: {
                ...state.usedAddresses,
                [walletId]: [...walletAddresses, {
                  address,
                  path,
                  firstUsed: now,
                  lastUsed: now
                }]
              }
            }
          }
        })
      },
      
      isAddressUsed: (walletId: string, address: string) => {
        const state = get()
        const walletAddresses = state.usedAddresses[walletId] || []
        return walletAddresses.some(a => a.address === address)
      },
      
      getUsedAddresses: (walletId: string) => {
        const state = get()
        const walletAddresses = state.usedAddresses[walletId] || []
        return new Set(walletAddresses.map(a => a.address))
      },
      
      clearWalletAddresses: (walletId: string) => {
        set((state) => {
          const { [walletId]: _, ...rest } = state.usedAddresses
          return { usedAddresses: rest }
        })
      },
      
      clearAll: () => {
        set({ usedAddresses: {} })
      }
    }),
    {
      name: 'wallet-addresses',
      version: 1
    }
  )
)